
import { Component, Vue, Watch, Prop } from "vue-property-decorator"
import { namespace, Getter, Action, State } from "vuex-class"
import vSelect from "vue-select"
// @ts-ignore
import VuePerfectScrollbar from "vue-perfect-scrollbar"

import { Field, FieldSelected, Rule, Trigger, dataTypeByFieldType, User } from "@/types"
import { DataType, Operator, operatorLabels, operatorsByFieldType } from "@/types/insights"

import CalculationDataType, { CalculationType as CalculatedFieldType, CalculationDataTypeValue } from "@/types/field"
import { caseColumns, CaseColumn } from "@/types/caseColumns"

// @ts-ignore
import cloneDeep from "lodash/cloneDeep"

import api from "@/services/api"
import FieldDropDown from "@/components/Config/Rules/FieldDropDown.vue"

import ParameterValue from "@/components/Controls/ParameterValue.vue"
import { UserWithFields } from "@/types/user"
import DataCache from "@/caching/caching"

const setup = namespace("setup")

@Component({
	components: {
		"v-select": vSelect,
		"field-dropdown": FieldDropDown,
		"parameter-value": ParameterValue,
		"scrollbar": VuePerfectScrollbar
	}
})
export default class RuleTrigger extends Vue {
	@Prop({ default: null })
	public rule!: Rule

	@Prop({ type: Boolean, default: true })
	public canEdit!: boolean;

	@State private readonly userCache!: DataCache<UserWithFields>

	@setup.Getter("getFieldTypes") fieldTypes!: any[];
	@setup.Getter("getFieldTypesById") fieldTypesById!: { [key: string]: any };
	@setup.Getter("getDataSources") dataSources!: any[];
	@setup.Getter("getDefinitions")	definitions!: [];
	@setup.Getter("getFilterableFields") filterableFields!: CaseColumn[];

	@setup.State fields!: Field[];

	private Operator = Operator;

	public triggerFields: CaseColumn[] = [];

	public fieldFilter: string = "";
	private operators: any[] = operatorLabels;
	private operator = Operator;
	private dataType = DataType;

	private isDebug = false;
	private debug (functionName: string, message: string) {
		if (this.isDebug) {
			console.log(`RuleTrigger.vue: ${functionName} >> ${message}`)
		}
	}

	public get columns () {
		return [
			{
				key: "label",
				label: "Title"
			},
			{
				key: "field",
				label: "Field"
			},
			{
				key: "operator",
				label: "Operator"
			},
			{
				key: "value",
				label: "Value"
			},
			{
				key: "",
				label: ""
			}
		]
	}

	public async mounted () {
		const allUsers = await this.userCache.fetchIfNeeded()
		this.triggerFields = this.filterableFields.filter(f => !f.operators || f.operators.length > 0)
			.map(c => cloneDeep(c))

		const closedAt = this.triggerFields.find(c => c.key === "closedAt")
		if (closedAt) {
			closedAt.operators = [Operator.IsSet]
		}

		const assignee = this.triggerFields.find(c => c.key === "assigneeId")
		if (assignee) {
			assignee.columnValues = () => new Promise((resolve, reject) => resolve(allUsers))
		}

		const collaborators = this.triggerFields.find(c => c.key === "users")
		if (collaborators) {
			collaborators.columnValues = () => new Promise((resolve, reject) => resolve(allUsers))
		}

		this.ruleChanged()
	}

	@Watch("rule")
	ruleChanged () {
		if (this.rule && this.rule.triggers) {
			this.rule.triggers.forEach((trigger: any) => {
				Vue.set(trigger, "fieldData", this.triggerFields.find(f => f.key === trigger.field))
			})
		}
	}

	public addTrigger () {
		// Create new dummy trigger and add it to the list of triggers
		let newTrigger: Trigger
		newTrigger = {
			id: "",
			label: "",
			field: "",
			subField: "",
			operator: 0,
			fieldDataType: 0,
			fieldTypeId: "",
			value: ""
		}

		this.rule.triggers.push(newTrigger)
	}

	public removeTrigger (trigger: Trigger) {
		if (!this.rule) {
			return
		}

		this.rule.triggers.removeWhere(t => t === trigger)
	}

	private getTrigger (trigger: Trigger) {
		return this.rule.triggers.find(t => t.id === trigger.id)
	}

	private optionsForField (field: CaseColumn) {
		var operators = field.operators ? field.operators : operatorsByFieldType[field.dataType]
		if (!operators) {
			return []
		}
		return operatorLabels.filter((o: any) => operators.indexOf(o.operator) > -1)
	}

	fieldChanged (changedParameter: any) {
		if (this.optionsForField(changedParameter.fieldData).find(o => o.operator === changedParameter.operator)) {
			changedParameter.operator = null
		}

		changedParameter.field = changedParameter.fieldData.key
		changedParameter.fieldDataType = changedParameter.fieldData.fieldDataType
	}

	dynamicValueChanged (trigger: any, newValue: boolean) {
		Vue.set(trigger, "dynamicValue", newValue)
		Vue.set(trigger, "value", null)
	}

	public addTriggerButtonDisabled () : boolean {
		let triggersNotValid: boolean = false

		if (this.rule.triggers.length > 0) {
			triggersNotValid = this.rule.triggers.some(t => t.label.length === 0)
			if (triggersNotValid) {
				this.$emit("invalid")
			} else {
				this.$emit("valid")
			}
		} else {
			triggersNotValid = false
			this.$emit("valid")
		}

		return triggersNotValid
	}
}

